// ============================================================
// VCS.ChatSetup — Registrierung, SMS-Verifikation, Settings
// ============================================================
window.VCS = window.VCS || {};

VCS.ChatSetup = class {

    // ── Konstanten ───────────────────────────────────────────

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._smsTimerInterval = null;
        this._smsPendingPhone = null;
        this._view = {};
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    async registerPhone() {
        const raw = $('#phoneInput')[0].value.trim();
        const phone = VCS.chat?.normalizePhone?.(raw);
        if (!phone) {
            if (raw) {
                VCS.ui?.showToast?.(t('phoneInvalid'), 'error', 5000);
            }
            return;
        }

        const name = ($('#regNameInput')[0]?.value || '').trim();
        if (!name) {
            VCS.ui?.showToast?.(t('nameRequired'), 'error', 4000);
            $('#regNameInput')[0]?.focus();
            return;
        }

        // E-Mail seit Auth-Hardening 2026-05-02 fuer alle Pflicht.
        const email = ($('#regEmailInput')[0]?.value || '').trim();
        if (!email) {
            VCS.ui?.showToast?.(t('emailRequired'), 'error', 4000);
            $('#regEmailInput')[0]?.focus();
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            VCS.ui?.showToast?.(t('emailInvalid'), 'error', 4000);
            $('#regEmailInput')[0]?.focus();
            return;
        }

        // Pruefen ob User schon existiert.
        // - Neu: Passwort-Setup-Dialog (zwei Felder).
        // - Existing: Passwort-Restore-Dialog im public-Mode — laedt /keys/backup-public,
        //   testet Decrypt clientseitig. Bei Fail bleibt der User im Dialog (kein send-code).
        //   Bei Erfolg ist privateKey schon im IndexedDB gespeichert.
        const exists = await this._checkPhoneExists(phone);
        let regPw = '';
        if (!exists) {
            regPw = await this.showPasswordSetupDialog();
            if (!regPw) {
                return;
            }
        } else {
            const verified = await VCS.e2e?.showRestoreDialog?.(phone, '', { public: true });
            // Public-Mode resolved mit PW-String bei Erfolg, false bei Abbruch / kein Backup.
            if (typeof verified !== 'string' || !verified) {
                return;
            }
            regPw = verified;
        }
        this._regPassword = regPw;

        const btn = $('#btnPhoneRegister')[0];
        if (btn) {
            btn.disabled = true;
        }

        try {
            const payload = {
                phone,
                displayName: name,
                fcmToken   : 'web-' + VCS.state.myId,
                userId     : 'web-' + VCS.state.myId,
                lang       : VCS.state.currentLang,
                password   : regPw
            };
            if (email) payload.email = email;
            const res = await fetch(VCS.Chat.API_BASE + '/auth/send-code', {
                method : 'POST',
                headers: Object.assign({'Content-Type': 'application/json'}, VCS.state.chatAuthToken ? {'Authorization': 'Bearer ' + VCS.state.chatAuthToken} : {}),
                body   : JSON.stringify(payload)
            });

            if (res.ok) {
                const result = await res.json().catch(() => ({}));
                if (result.whitelisted) {
                    VCS.state.chatMyPhone = phone;
                    VCS.state.chatMyName = name;
                    VCS.state.chatMyPlan = result.plan || 'free';
                    if (result.trialEndsAt) {
                        VCS.state.chatTrialEndsAt = result.trialEndsAt;
                        localStorage.setItem('chat-trial-ends-at', String(result.trialEndsAt));
                    }
                    if (result.token) {
                        VCS.state.chatAuthToken = result.token;
                        localStorage.setItem('chat-auth-token', result.token);
                    }
                    localStorage.setItem('chat-phone', phone);
                    localStorage.setItem('chat-name', name);
                    localStorage.setItem('chat-plan', VCS.state.chatMyPlan);
                    VCS.chat?.notifySwPhone?.();
                    VCS.state.chatRegistered = true;
                    this.updateRegistrationUI();
                    VCS.signaling?.send({
                        type : 'chat-auth',
                        phone: VCS.state.chatMyPhone,
                        token: VCS.state.chatAuthToken,
                    });
                    await VCS.e2e?.handleKeySetup?.(phone, regPw, result.existing, result.hasBackup);
                    VCS.nav?.showAppShell?.('tabChats');
                    // Welcome-Overlay für neue Trial-User
                    if (result.trialEndsAt && !result.existing) {
                        setTimeout(() => this.showWelcomeOverlay(), 500);
                    } else {
                        // Notification-Prompt nur wenn kein Welcome-Overlay
                        setTimeout(() => VCS.notify?.promptAfterRegistration(), 800);
                    }
                } else {
                    this._smsPendingPhone = phone;
                    this._verifyMethod = result.method || 'sms';
                    this.showSmsVerifyUI();
                }
            } else {
                const err = await res.json().catch(() => ({}));
                if (err.error === 'sms_rate_limit') {
                    VCS.ui?.showToast?.(t('smsRateLimit').replace('{0}', err.wait || 60), 'error', 5000);
                } else if (err.error === 'wrong_password') {
                    VCS.ui?.showToast?.(t('keyRestoreWrongPassword'), 'error', 5000);
                } else if (err.error === 'account_inactive') {
                    VCS.ui?.showToast?.(t('accountInactive'), 'error', 6000);
                } else {
                    VCS.ui?.showToast?.(err.error || 'SMS senden fehlgeschlagen', 'error');
                }
            }
        } catch (e) {
            console.error('[chat] send-code error:', e);
            VCS.ui?.showToast?.('Network error', 'error');
        } finally {
            if (btn) {
                btn.disabled = false;
            }
        }
    }

    // Login auf neuem Geraet via E-Mail + E2E-Passwort.
    // Server kennt das PW NICHT; er schickt nur einen Mail-Code an die hinterlegte
    // Adresse. Das eingegebene PW wird lokal in `_loginPassword` gehalten und nach
    // erfolgreicher Code-Verifikation zum lokalen Decrypt des PrivateKeys benutzt
    // (handleKeySetup in verifySmsCode).
    async loginWithEmail() {
        const email = ($('#loginEmailInput')[0]?.value || '').trim();
        const password = ($('#loginPasswordInput')[0]?.value || '');
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            VCS.ui?.showToast?.(t('emailInvalid'), 'error', 4000);
            $('#loginEmailInput')[0]?.focus();
            return;
        }
        if (!password || password.length < 8) {
            VCS.ui?.showToast?.(t('loginPasswordRequired'), 'error', 4000);
            $('#loginPasswordInput')[0]?.focus();
            return;
        }

        const btn = $('#btnLoginEmail')[0];
        if (btn) { btn.disabled = true; }
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/auth/login-request', {
                method : 'POST',
                headers: {'Content-Type': 'application/json'},
                body   : JSON.stringify({email, lang: VCS.state.currentLang || 'de'}),
            });
            const result = await res.json().catch(() => ({}));
            if (res.ok && result.ok && result.phone) {
                this._authMode = 'login';
                this._smsPendingPhone = result.phone;
                this._smsPendingEmail = email;
                this._loginPassword = password;
                this._verifyMethod = 'email';
                this.showSmsVerifyUI();
            } else if (result.error === 'user_not_found') {
                VCS.ui?.showToast?.(t('loginEmailUnknown'), 'error', 5000);
            } else if (result.error === 'rate_limit') {
                VCS.ui?.showToast?.(t('smsRateLimit').replace('{0}', result.wait || 60), 'error', 5000);
            } else if (result.error === 'email_invalid') {
                VCS.ui?.showToast?.(t('emailInvalid'), 'error', 4000);
            } else {
                VCS.ui?.showToast?.(result.error || t('loginFailed'), 'error', 4000);
            }
        } catch (e) {
            console.error('[chat] login-request error:', e);
            VCS.ui?.showToast?.('Network error', 'error');
        } finally {
            if (btn) { btn.disabled = false; }
        }
    }

    showSmsVerifyUI() {
        const overlay = $('#smsVerifyOverlay')[0];
        if (!overlay) {
            return;
        }

        // Beide Reg/Login-Cards ausblenden
        document.querySelectorAll('.reg-card').forEach((el) => el.classList.add('hidden'));

        // Hinweistext anpassen bei E-Mail-Verifizierung
        const hintEl = $('#smsVerifyHintText')[0];
        if (hintEl && this._verifyMethod === 'email') {
            hintEl.textContent = t('emailVerifyCodeHint');
        }

        overlay.classList.remove('hidden');
        document.querySelectorAll('.reg-otp-cell').forEach((el) => {
            el.value = '';
        });
        const hidden = $('#smsCodeInput')[0];
        if (hidden) {
            hidden.value = '';
        }
        document.querySelector('.reg-otp-cell')?.focus();

        this.startSmsTimer(600);
    }

    startSmsTimer(seconds) {
        if (this._smsTimerInterval) {
            clearInterval(this._smsTimerInterval);
        }
        const timerEl = $('#smsTimer')[0];
        const resendBtn = $('#btnResendSms')[0];
        if (resendBtn) {
            resendBtn.classList.add('hidden');
        }

        let remaining = seconds;
        const tick = () => {
            const m = Math.floor(remaining / 60);
            const s = remaining % 60;
            if (timerEl) {
                timerEl.textContent = t('smsTimerText').replace('{0}', `${m}:${s.toString().padStart(2, '0')}`);
            }
            if (remaining <= 0) {
                clearInterval(this._smsTimerInterval);
                this._smsTimerInterval = null;
                if (timerEl) {
                    timerEl.textContent = t('smsCodeExpired');
                }
                if (resendBtn) {
                    resendBtn.classList.remove('hidden');
                }
            }
            remaining--;
        };
        tick();
        this._smsTimerInterval = setInterval(tick, 1000);
    }

    async verifySmsCode() {
        const code = ($('#smsCodeInput')[0]?.value || '').trim();
        if (!code || code.length < 6) {
            VCS.ui?.showToast?.(t('smsCodeInvalid'), 'error', 3000);
            return;
        }
        if (!this._smsPendingPhone) {
            return;
        }

        const btn = $('#btnVerifySms')[0];
        if (btn) {
            btn.disabled = true;
        }

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/auth/verify-sms', {
                method : 'POST',
                headers: Object.assign({'Content-Type': 'application/json'}, VCS.state.chatAuthToken ? {'Authorization': 'Bearer ' + VCS.state.chatAuthToken} : {}),
                body   : JSON.stringify({
                    phone: this._smsPendingPhone,
                    code
                })
            });

            const result = await res.json().catch(() => ({}));

            if (res.ok && result.ok) {
                // SMS-Overlay ausblenden
                $('#smsVerifyOverlay')[0]?.classList.add('hidden');
                if (this._smsTimerInterval) {
                    clearInterval(this._smsTimerInterval);
                    this._smsTimerInterval = null;
                }
                const isLoginMode = this._authMode === 'login';
                // Name: Reg-Mode aus Eingabe, Login-Mode aus Server-Response (oder Bestand).
                const name = isLoginMode
                    ? (result.displayName || localStorage.getItem('chat-name') || '')
                    : ($('#regNameInput')[0]?.value || '').trim();
                // Passwort fuer E2E-Setup: im Reg-Mode das gerade vergebene, im Login-Mode
                // das vom User im Login-Form eingetippte. Server hat es nie gesehen.
                const e2ePassword = isLoginMode ? (this._loginPassword || '') : (this._regPassword || '');

                // ── Login-Mode: E2E-Decrypt VOR Session-Persistierung ──────────────
                // Wenn das eingegebene PW den Backup-PrivateKey nicht entschluesseln
                // kann, wird die ganze Session verworfen. Sonst koennte der User mit
                // gueltigem Code aber falschem PW per F5 in den Chat einsteigen
                // (Token waere bereits in localStorage, aber kein Key entschluesselbar).
                // Token-Zugriff in autoRestore braucht VCS.state.chatAuthToken — wird
                // hier nur tempor​aer im RAM gesetzt, NICHT persistiert.
                if (isLoginMode) {
                    if (result.token) { VCS.state.chatAuthToken = result.token; }
                    let restored = await VCS.e2e?.autoRestore?.(this._smsPendingPhone, e2ePassword);
                    if (!restored) {
                        restored = await VCS.e2e?.showRestoreDialog?.(this._smsPendingPhone, e2ePassword);
                    }
                    if (!restored) {
                        // Session zurueckrollen — nichts in localStorage, nichts in state.
                        VCS.state.chatAuthToken = '';
                        this._loginPassword = '';
                        this._authMode = null;
                        this._smsPendingPhone = null;
                        VCS.ui?.showToast?.(t('loginPasswordWrong') || 'Passwort falsch — Anmeldung abgebrochen.', 'error', 5000);
                        // Login-Card wieder zeigen
                        document.querySelectorAll('.reg-card').forEach((el) => el.classList.add('hidden'));
                        $('#regCardLogin')[0]?.classList.remove('hidden');
                        $('#loginPasswordInput')[0]?.focus();
                        return;
                    }
                }

                // Ab hier ist (a) entweder Reg-Mode oder (b) Login-Mode mit erfolgreichem Decrypt.
                VCS.state.chatMyPhone = this._smsPendingPhone;
                VCS.state.chatMyName = name;
                VCS.state.chatMyPlan = result.plan || 'free';
                if (result.lang) {
                    VCS.state.currentLang = result.lang;
                }
                if (result.trialEndsAt) {
                    VCS.state.chatTrialEndsAt = result.trialEndsAt;
                    localStorage.setItem('chat-trial-ends-at', String(result.trialEndsAt));
                }
                if (result.token) {
                    VCS.state.chatAuthToken = result.token;
                    localStorage.setItem('chat-auth-token', result.token);
                }
                localStorage.setItem('chat-phone', this._smsPendingPhone);
                localStorage.setItem('chat-name', name);
                localStorage.setItem('chat-plan', VCS.state.chatMyPlan);
                VCS.chat?.notifySwPhone?.();
                VCS.state.chatRegistered = true;
                this.updateRegistrationUI();
                VCS.signaling?.send({
                    type : 'chat-auth',
                    phone: VCS.state.chatMyPhone,
                    token: VCS.state.chatAuthToken,
                });
                // handleKeySetup nur im Reg-Mode — im Login-Mode haben wir schon
                // erfolgreich restored. handleKeySetup wuerde sonst redundant laufen
                // oder bei einem fail-through-Pfad einen NEUEN Key generieren
                // (project_e2e_key_mismatch_bug-Falle).
                if (!isLoginMode) {
                    await VCS.e2e?.handleKeySetup?.(this._smsPendingPhone, e2ePassword, result.existing, result.hasBackup);
                }
                this._smsPendingPhone = null;
                this._loginPassword = '';
                this._authMode = null;
                VCS.nav?.showAppShell?.('tabChats');
                // Welcome-Overlay nur fuer echte Neu-User (nicht Login-Pfad).
                if (!isLoginMode && result.trialEndsAt && !result.existing) {
                    setTimeout(() => this.showWelcomeOverlay(), 500);
                } else {
                    setTimeout(() => VCS.notify?.promptAfterRegistration(), 800);
                }
            } else {
                if (result.error === 'wrong_code') {
                    VCS.ui?.showToast?.(t('smsWrongCode').replace('{0}', result.attemptsLeft ?? '?'), 'error', 4000);
                } else if (result.error === 'code_expired') {
                    VCS.ui?.showToast?.(t('smsCodeExpired'), 'error', 5000);
                } else if (result.error === 'too_many_attempts') {
                    VCS.ui?.showToast?.(t('smsTooManyAttempts'), 'error', 6000);
                    this.resetSmsVerifyUI();
                } else if (result.error === 'wrong_password') {
                    VCS.ui?.showToast?.(t('keyRestoreWrongPassword'), 'error', 5000);
                    this.resetSmsVerifyUI();
                } else if (result.error === 'account_inactive') {
                    VCS.ui?.showToast?.(t('accountInactive'), 'error', 6000);
                    this.resetSmsVerifyUI();
                } else {
                    VCS.ui?.showToast?.(result.error || 'Verifizierung fehlgeschlagen', 'error');
                }
            }
        } catch (e) {
            console.error('[chat] verify-sms error:', e);
            VCS.ui?.showToast?.('Network error', 'error');
        } finally {
            if (btn) {
                btn.disabled = false;
            }
        }
    }

    async resendSmsCode() {
        if (!this._smsPendingPhone) {
            return;
        }
        const btn = $('#btnResendSms')[0];
        if (btn) {
            btn.disabled = true;
        }

        try {
            const name = ($('#regNameInput')[0]?.value || '').trim();
            const email = ($('#regEmailInput')[0]?.value || '').trim();
            const payload = {
                phone      : this._smsPendingPhone,
                displayName: name,
                fcmToken   : 'web-' + VCS.state.myId,
                userId     : 'web-' + VCS.state.myId,
                lang       : VCS.state.currentLang,
                password   : this._regPassword || ''
            };
            if (email) payload.email = email;
            const res = await fetch(VCS.Chat.API_BASE + '/auth/send-code', {
                method : 'POST',
                headers: Object.assign({'Content-Type': 'application/json'}, VCS.state.chatAuthToken ? {'Authorization': 'Bearer ' + VCS.state.chatAuthToken} : {}),
                body   : JSON.stringify(payload)
            });

            if (res.ok) {
                VCS.ui?.showToast?.(t('smsResent'), 'success', 3000);
                document.querySelectorAll('.reg-otp-cell').forEach((el) => {
                    el.value = '';
                });
                const hidden = $('#smsCodeInput')[0];
                if (hidden) {
                    hidden.value = '';
                }
                document.querySelector('.reg-otp-cell')?.focus();
                this.startSmsTimer(600);
            } else {
                const err = await res.json().catch(() => ({}));
                if (err.error === 'sms_rate_limit') {
                    VCS.ui?.showToast?.(t('smsRateLimit').replace('{0}', err.wait || 60), 'error', 5000);
                } else {
                    VCS.ui?.showToast?.(err.error || 'SMS senden fehlgeschlagen', 'error');
                }
            }
        } catch (e) {
            VCS.ui?.showToast?.('Network error', 'error');
        } finally {
            if (btn) {
                btn.disabled = false;
            }
        }
    }

    resetSmsVerifyUI() {
        const overlay = $('#smsVerifyOverlay')[0];
        if (overlay) {
            overlay.classList.add('hidden');
        }
        // Registrierungsbox wieder einblenden
        $('.reg-card')[0]?.classList.remove('hidden');

        if (this._smsTimerInterval) {
            clearInterval(this._smsTimerInterval);
            this._smsTimerInterval = null;
        }
        this._smsPendingPhone = null;

        const ci = $('#smsCodeInput')[0];
        if (ci) {
            ci.value = '';
        }
        document.querySelectorAll('.reg-otp-cell').forEach((el) => {
            el.value = '';
        });
    }

    // Validiert den localStorage-Token gegen /auth/me beim App-Start.
    // Bei 401/403 wird der lokale Login invalidiert und auf die Reg-Maske zurueckgesetzt.
    async validateAuthToken() {
        if (!VCS.state.chatAuthToken || !VCS.state.chatMyPhone) {
            return false;
        }
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/auth/me', {
                headers: { 'Authorization': 'Bearer ' + VCS.state.chatAuthToken }
            });
            if (res.status === 401 || res.status === 403) {
                const err = await res.json().catch(() => ({}));
                console.warn('[chat] auth/me invalid:', res.status, err.error || '');
                this.invalidateLocalAuth(err.error || 'invalid_session');
                return false;
            }
            if (!res.ok) {
                console.warn('[chat] auth/me unexpected status:', res.status);
                return true;
            }
            return true;
        } catch (e) {
            console.warn('[chat] auth/me network error:', e.message);
            return true;
        }
    }

    invalidateLocalAuth(reason) {
        console.warn('[chat] invalidateLocalAuth:', reason);
        const keys = [
            'chat-phone', 'chat-name', 'chat-auth-token', 'chat-plan',
            'chat-plan-paid', 'chat-plan-live-quota', 'chat-can-buy-minutes', 'chat-plan-name',
            'chat-trial-ends-at', 'chat-pro-trial-used', 'chat-pro-trial-total',
            'chat-live-cap-used', 'chat-live-cap-total',
            'chat-live-cap-group-used', 'chat-live-cap-group-total',
            'chat-live-cap-period-start', 'chat-live-extra', 'chat-avatar-hash',
        ];
        keys.forEach((k) => localStorage.removeItem(k));
        VCS.state.chatMyPhone = null;
        VCS.state.chatMyName = '';
        VCS.state.chatMyPlan = 'free';
        // Muss mit zurueck auf false: sonst behaelt ein abgemeldetes Konto die
        // freigeschalteten Funktionen, bis der naechste Server-Push kommt.
        VCS.state.chatPlanPaid = false;
        VCS.state.chatPlanLiveQuota = false;
        VCS.state.chatCanBuyMinutes = false;
        VCS.state.chatPlanDisplayName = '';
        VCS.state.chatAuthToken = null;
        VCS.state.chatRegistered = false;
        if (reason === 'account_inactive') {
            VCS.ui?.showToast?.(t('accountInactive'), 'error', 6000);
        }
        location.reload();
    }

    showWelcomeOverlay() {
        const $overlay = $('#welcomeOverlay');
        const $text = $('#welcomeText');
        const $ok = $('#welcomeOk');
        if (!$overlay.length) return;
        $text.html(t('welcomeText'));
        $overlay.removeClass('hidden');
        const cleanup = () => {
            $overlay.addClass('hidden');
            $ok.off('click');
            $overlay.off('click');
            // Notification-Prompt nach dem Schließen
            setTimeout(() => VCS.notify?.promptAfterRegistration(), 300);
        };
        $ok.on('click', cleanup);
        $overlay.on('click', (e) => {
            if (e.target === $overlay[0]) cleanup();
        });
    }

    async unregister() {
        if (!VCS.state.chatMyPhone) {
            return;
        }
        if (!await VCS.ui?.showConfirm?.(t('logoutConfirm'), '', t('confirmLogout'))) {
            return;
        }

        // Push-Subscription beim Server abmelden
        VCS.signaling?.send({ type: 'push-unsubscribe' });

        try {
            await fetch(VCS.Chat.API_BASE + '/unregister', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    phoneNumber: VCS.state.chatMyPhone,
                    fcmToken   : 'web-' + VCS.state.myId
                })
            });
        } catch (e) {
            console.error('[chat] logout error:', e);
        }

        await VCS.session?.clearAllClientData();
        location.reload();
    }

    async forceLogout() {
        VCS.state._accountKicked = true;
        await VCS.session?.clearAllClientData();
        location.reload();
    }

    // Pre-render gate: a different device may have deleted/re-registered this
    // phone while we were offline. Before we render any cached chat data, ask
    // the server "is this token still valid for the user we think we are?".
    // - 200 OK: continue, render cache.
    // - 401: account is gone or session was invalidated -> wipe locally and
    //   reload to the login screen.
    // - network error: keep the cache, but show a small offline hint so the
    //   user knows the data may be stale.
    async verifyAccountStillExists() {
        if (!VCS.state.chatRegistered || !VCS.state.chatAuthToken) {
            return true; // nothing to verify
        }
        let res;
        try {
            res = await fetch(VCS.Chat.API_BASE + '/auth/me', {
                method : 'GET',
                headers: { 'Authorization': 'Bearer ' + VCS.state.chatAuthToken },
                cache  : 'no-store'
            });
        } catch (e) {
            // Real offline / DNS / TLS error. Keep the local cache, hint the user.
            console.warn('[chat] verifyAccount: server unreachable, keeping cache:', e?.message || e);
            try { VCS.ui?.showToast?.('Offline — Daten möglicherweise veraltet', 'info', 4000); } catch (_) {}
            return true;
        }
        if (res.ok) {
            return true;
        }
        if (res.status === 401) {
            // Token rejected. Either expired or the account was deleted /
            // re-registered. Either way: drop everything locally.
            console.warn('[chat] verifyAccount: 401, dropping local state');
            try {
                if (navigator.serviceWorker?.controller) {
                    navigator.serviceWorker.controller.postMessage({ type: 'set-phone', phone: null });
                }
            } catch (_) {}
            VCS.state._accountKicked = true;
            await VCS.session?.clearAllClientData();
            location.reload();
            return false;
        }
        // 404/500 -> do not kick, server has its own problem.
        console.warn('[chat] verifyAccount: unexpected status', res.status);
        return true;
    }

    async deleteAccount() {
        if (!VCS.state.chatMyPhone) {
            return;
        }

        // Pre-Check: aktives Stripe-Abo? Wenn ja, User auf Webseite verweisen.
        // /auth/me liest den bernoria_session-Cookie (Domain .bernoria.com,
        // also auch auf messenger-Subdomain verfuegbar). Bei 401/Network-Error
        // fallen wir in den existierenden Confirm-Dialog (User ohne Web-Account).
        let hasSubscription = false;
        try {
            const meRes = await fetch(VCS.Chat.API_BASE + '/auth/me', { credentials: 'include' });
            if (meRes.ok) {
                const meData = await meRes.json();
                hasSubscription = !!(meData && meData.user && meData.user.hasSubscription);
            }
        } catch (_) { /* fall through to normal confirm */ }

        if (hasSubscription) {
            this._showDeleteAccountSubModal();
            return;
        }

        if (!await VCS.ui?.showConfirm?.(t('deleteAccountConfirm'), '', t('confirmDelete'))) {
            return;
        }

        try {
            const resp = await fetch(VCS.Chat.API_BASE + '/unregister', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({phoneNumber: VCS.state.chatMyPhone})
            });
            const result = await resp.json();
            if (!resp.ok || result.status === 'not_found') {
                console.error('[chat] delete account failed:', result);
                VCS.ui?.showToast?.(t('errorDeleteAccount') || 'Account konnte nicht gelöscht werden', 'error');
                return;
            }
        } catch (e) {
            console.error('[chat] delete account error:', e);
            VCS.ui?.showToast?.(t('errorDeleteAccount') || 'Server nicht erreichbar', 'error');
            return;
        }

        await VCS.session?.clearAllClientData();
        location.reload();
    }

    // Modal-Helper fuer den Sub-Pfad (aktives Stripe-Abo): Hinweis + Button
    // "Zur Webseite", der explizit per User-Klick https://www.bernoria.com/account
    // im neuen Tab oeffnet (kein Auto-Redirect).
    _showDeleteAccountSubModal() {
        const $overlay = $('#deleteAccountSubOverlay');
        const $cancel  = $('#deleteAccountSubCancel');
        const $goWeb   = $('#deleteAccountSubGoWeb');
        if (!$overlay.length) return;

        const cleanup = () => {
            $overlay.addClass('hidden');
            $cancel.off('click');
            $goWeb.off('click');
            $overlay.off('click');
        };

        $overlay.removeClass('hidden');
        $cancel.on('click', cleanup);
        $goWeb.on('click', () => {
            window.open('https://www.bernoria.com/account', '_blank', 'noopener');
            cleanup();
        });
        $overlay.on('click', (e) => {
            if (e.target === $overlay[0]) cleanup();
        });
    }

    updateRegistrationUI() {
        const phoneInput = $('#phoneInput')[0];
        const nameInput = $('#regNameInput')[0];
        const phoneStatus = $('#phoneStatus')[0];
        const btnRegister = $('#btnPhoneRegister')[0];
        if (!phoneInput) {
            return;
        }

        if (VCS.state.chatRegistered && VCS.state.chatMyPhone) {
            phoneInput.value = VCS.state.chatMyPhone;
            phoneInput.readOnly = true;
            if (nameInput) {
                nameInput.value = VCS.state.chatMyName;
                nameInput.readOnly = true;
            }
            btnRegister?.classList.add('hidden');
            if (phoneStatus) {
                phoneStatus.textContent = t('phoneRegistered').replace('{0}', VCS.state.chatMyPhone);
                phoneStatus.classList.remove('hidden');
            }
        } else {
            phoneInput.readOnly = false;
            if (nameInput) {
                nameInput.readOnly = false;
            }
            btnRegister?.classList.remove('hidden');
            phoneStatus?.classList.add('hidden');
        }
        this.updateSettingsUI();
        this.updateProfileUI();
    }

    async saveName() {
        const newName = ($('#profileNameInput')[0]?.value || '').trim();
        if (!newName || !VCS.state.chatMyPhone) {
            return;
        }
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/update-profile', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    phoneNumber: VCS.state.chatMyPhone,
                    displayName: newName
                })
            });
            if (res.ok) {
                VCS.state.chatMyName = newName;
                try { localStorage.setItem('chat-name', newName); } catch (_) {}
                const regInput = $('#regNameInput')[0];
                if (regInput) {
                    regInput.value = newName;
                }
                $('#btnProfileSaveName')[0]?.classList.add('hidden');
                this.updateProfileUI();
                VCS.ui?.showToast(t('nameSaved'), 'success', 3000);
            } else {
                VCS.ui?.showToast(t('nameChangeError'), 'error', 4000);
            }
        } catch (e) {
            console.error('[settings] name update failed:', e);
            VCS.ui?.showToast(t('nameChangeError'), 'error', 4000);
        }
    }

    // Dating: kein Plan-Feature, eigener Ein/Aus-Schalter vom Backend (chat-authed
    // beim Start, dating-updated bei Live-Aenderung durchs Admin-Tool). Kein
    // localStorage-Cache -- fail-closed, solange kein frischer Wert vom Server da ist.
    applyDatingEnabled(enabled) {
        VCS.state.datingEnabled = enabled;
        $('[data-tab="tabDating"]').toggleClass('hidden', !enabled);
        // Steht der Nutzer gerade im Dating-Tab/-Screen und wird live abgeschaltet
        // (Admin-Toggle waehrend offener Session), zurueck zu den Chats.
        if (!enabled && (VCS.state.activeTabId === 'tabDating' || !$('#datingScreen').hasClass('hidden'))) {
            VCS.nav?.switchTab?.('tabChats');
        }
    }

    updateSettingsUI() {
        // Name + Phone sind jetzt im Profil-Overlay
        const nameInput = $('#profileNameInput')[0];
        if (nameInput) {
            nameInput.value = VCS.state.chatMyName || '';
            nameInput.disabled = !VCS.state.chatRegistered;
        }
        $('#btnProfileSaveName')[0]?.classList.add('hidden');

        const el = $('#profilePhoneDisplay')[0];
        if (el) {
            el.textContent = VCS.state.chatRegistered && VCS.state.chatMyPhone
                ? VCS.state.chatMyPhone
                : t('phonePlaceholder');
        }
        const s = VCS.state;
        const trialEnd = s.chatTrialEndsAt;
        const inTrial = trialEnd && Date.now() < trialEnd;
        // Trial-UI haengt allein an trialEndsAt. Vorher stand hier zusaetzlich
        // `s.chatMyPlan === 'chat'` als Trial-Marker -- das war schon damals nur
        // eine Absicherung (zahlende Konten bekommen trialEndsAt=0 vom
        // Stripe-Webhook) und waere ab der Tarifumstellung schlicht falsch: der
        // Trial hebt jetzt auf 'plus', und dann greift der Namensvergleich nie mehr.
        const showTrialUI = inTrial;
        const proTotal = s.chatProTrialSecondsTotal || 0;
        const proUsed = s.chatProTrialSecondsUsed || 0;
        const proRemaining = Math.max(0, proTotal - proUsed);

        // chatMyPlan ist bereits der EFFEKTIVE Plan: der Server hebt den Trial in
        // getEffectivePlan an, bevor er ihn in 'chat-authed'/'plan-updated'
        // schickt. Die fruehere Anhebung auf 'chat' an dieser Stelle war doppelt
        // gemoppelt und haette nach der Umstellung einen Tarif gesetzt, den es
        // nicht mehr gibt.
        const effectivePlan = s.chatMyPlan || 'free';

        // Plan-Name
        const planNameEl = $('#settingsPlanName')[0];
        if (planNameEl) {
            // Anzeigename kommt aus plan_config vom Server. Fruehere Namensliste
            // im Client kannte plus/premium nicht und zeigte dann den technischen
            // Schluessel. Fallback bleibt der Schluessel, nie eine leere Zeile.
            planNameEl.textContent = s.chatPlanDisplayName || effectivePlan;
        }

        // Trial-Restzeit
        const trialEl = $('#settingsPlanTrial')[0];
        if (trialEl) {
            if (showTrialUI) {
                const remaining = trialEnd - Date.now();
                const hours = Math.ceil(remaining / (1000 * 60 * 60));
                const days = Math.ceil(remaining / (1000 * 60 * 60 * 24));
                const timeStr = days >= 1
                    ? t('trialDays').replace('{n}', days)
                    : t('trialHours').replace('{n}', hours);
                trialEl.textContent = t('trialRemaining').replace('{days}', timeStr);
                trialEl.classList.remove('hidden');
            } else {
                trialEl.textContent = '';
                trialEl.classList.add('hidden');
            }
        }

        // Pro-Trial
        const proTrialEl = $('#settingsProTrial')[0];
        if (proTrialEl) {
            const showPro = showTrialUI && proTotal > 0;
            proTrialEl.classList.toggle('hidden', !showPro);
            if (showPro) {
                const proMin = Math.floor(proRemaining / 60);
                const proSec = proRemaining % 60;
                const label = $('#settingsProTrialLabel')[0];
                const bar = $('#settingsProTrialProgress')[0];
                if (label) {
                    const timeStr = proMin + ':' + String(proSec).padStart(2, '0');
                    label.textContent = proRemaining > 0
                        ? (t('proTrialLabel') || 'Live-Übersetzung testen') + ' — ' + timeStr + ' min'
                        : (t('proTrialExpired') || '');
                }
                if (bar) bar.style.width = Math.round((proRemaining / proTotal) * 100) + '%';
            }
        }

        // Live-Cap (300 min/month for plan='live'; sub-cap 60 min for groups with 3+ languages)
        // State is fed by 'live-cap-update' WS event (signaling.js). Sub-cap block stays
        // hidden when backend hasn't sent group counters yet.
        const liveCapEl = $('#settingsLiveCap')[0];
        if (liveCapEl) {
            // Plan-Eigenschaft statt Plan-Name (2026-08-30): der Balken gehoert zu
            // jedem Tarif mit eigenem Live-Kontingent, nicht zu einem bestimmten.
            const isLive = !!s.chatPlanLiveQuota;
            const liveTotal = s.chatLiveCapSecondsTotal || 0;
            const liveUsed  = s.chatLiveCapSecondsUsedMonth || 0;
            // Flatrate (admin switch per user): usage keeps counting, the cap doesn't apply.
            const unlimited = !!s.chatLiveCapUnlimited;
            const showLive  = isLive && liveTotal > 0;
            liveCapEl.classList.toggle('hidden', !showLive);
            if (showLive) {
                const usedMin  = Math.floor(liveUsed  / 60);
                const totalMin = Math.floor(liveTotal / 60);
                const remainingMin = Math.max(0, totalMin - usedMin);
                const pct = Math.min(100, Math.max(0, (liveUsed / liveTotal) * 100));

                // Reset date: first day of next month, formatted in user's UI lang.
                const next = new Date();
                next.setMonth(next.getMonth() + 1);
                next.setDate(1);
                let dateStr;
                try {
                    dateStr = next.toLocaleDateString(s.currentLang || 'de-DE', { day: 'numeric', month: 'long' });
                } catch (_) {
                    dateStr = next.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' });
                }

                // Main bar label: "Live-Uebersetzung: 142 von 300 Min genutzt"
                const labelEl = $('#settingsLiveCapLabel')[0];
                if (labelEl) {
                    labelEl.textContent = (t('liveCapRemaining') || '')
                        .replace('{used}',  String(usedMin))
                        .replace('{total}', String(totalMin));
                }

                // Main bar fill + colour stage (inline-style, no SCSS edit).
                const progressEl = $('#settingsLiveCapProgress')[0];
                if (progressEl) {
                    progressEl.style.width = pct + '%';
                    if (pct >= 100)     progressEl.style.background = '#ef4444';   // red, exceeded
                    else if (pct >= 80) progressEl.style.background = '#f59e0b';   // amber, warn
                    else                progressEl.style.background = '#f97316';   // accent orange
                }

                // Sub-cap: 60 min for groups with 3+ languages. Only visible when
                // backend has shipped a positive total counter.
                const groupTotal = s.chatLiveCapGroupSecondsTotal || 0;
                const groupUsed  = s.chatLiveCapGroupSecondsUsed  || 0;
                const groupEl = $('#settingsLiveCapGroup')[0];
                if (groupEl) {
                    const showGroup = groupTotal > 0;
                    groupEl.classList.toggle('hidden', !showGroup);
                    if (showGroup) {
                        const gUsedMin  = Math.floor(groupUsed  / 60);
                        const gTotalMin = Math.floor(groupTotal / 60);
                        const gPct = Math.min(100, Math.max(0, (groupUsed / groupTotal) * 100));
                        const gLabel = $('#settingsLiveCapGroupLabel')[0];
                        if (gLabel) {
                            gLabel.textContent = (t('liveCapGroupLabel') || 'Davon Gruppen mit 3+ Sprachen')
                                + ' — ' + gUsedMin + ' / ' + gTotalMin + ' ' + (t('minutesShort') || 'Min');
                        }
                        const gBar = $('#settingsLiveCapGroupProgress')[0];
                        if (gBar) {
                            gBar.style.width = gPct + '%';
                            if (gPct >= 100)     gBar.style.background = '#ef4444';
                            else if (gPct >= 80) gBar.style.background = '#f59e0b';
                            else                 gBar.style.background = '#f97316';
                        }
                    }
                }

                // Status line above reset hint: warn at 80%+, exceeded at 100%.
                // Group-exceeded takes precedence when total still has headroom.
                const statusEl = $('#settingsLiveCapStatus')[0];
                if (statusEl) {
                    const groupPct = groupTotal > 0 ? (groupUsed / groupTotal) * 100 : 0;
                    if (pct >= 100) {
                        statusEl.textContent = (t('liveCapExceeded') || '').replace('{date}', dateStr);
                        statusEl.style.color = '#ef4444';
                        statusEl.classList.remove('hidden');
                    } else if (groupTotal > 0 && groupPct >= 100) {
                        statusEl.textContent = (t('liveCapGroupExceeded') || '').replace('{date}', dateStr);
                        statusEl.style.color = '#ef4444';
                        statusEl.classList.remove('hidden');
                    } else if (pct >= 80) {
                        statusEl.textContent = (t('liveCapWarn80') || '').replace('{remaining}', String(remainingMin));
                        statusEl.style.color = '#f59e0b';
                        statusEl.classList.remove('hidden');
                    } else if (groupTotal > 0 && groupPct >= 80) {
                        statusEl.textContent = (t('liveCapGroupWarn80') || 'Gruppen-Limit fast erreicht.');
                        statusEl.style.color = '#f59e0b';
                        statusEl.classList.remove('hidden');
                    } else {
                        statusEl.textContent = '';
                        statusEl.classList.add('hidden');
                    }
                }

                // Reset hint always shown for live users.
                const resetEl = $('#settingsLiveCapReset')[0];
                if (resetEl) {
                    resetEl.textContent = (t('liveCapResetAt') || '').replace('{date}', dateStr);
                }

                // Flatrate: no quota, so no bar, no warnings and no reset date. Usage
                // stays visible because the server keeps counting it. Placed last on
                // purpose: the quota rendering above stays untouched and is only
                // overwritten here when the flat rate is on.
                const barEl = progressEl ? progressEl.parentElement : null;
                if (barEl) barEl.classList.toggle('hidden', unlimited);
                if (unlimited) {
                    if (labelEl) {
                        labelEl.textContent = (t('liveCapUnlimited') || 'Live-Übersetzung: unbegrenzt')
                            + ' — ' + usedMin + ' ' + (t('minutesShort') || 'Min');
                    }
                    if (groupEl)  groupEl.classList.add('hidden');
                    if (statusEl) { statusEl.textContent = ''; statusEl.classList.add('hidden'); }
                    if (resetEl)  resetEl.textContent = '';
                }
            }
        }

        // Minutenkauf (2026-08-30): eigener Block, weil Plus-Kunden kein
        // Kontingent haben (also keinen Balken oben), aber kaufen duerfen.
        // Bei Flatrate ausgeblendet -- wer kein Limit hat, braucht keine Minuten.
        //
        // SEHEN und KAUFEN sind zwei verschiedene Bedingungen. Bis hierher hingen
        // beide an chatPlanPaid, und wer nach einem Downgrade auf Free noch
        // Guthaben hatte, sah es nirgends mehr -- obwohl gekaufte Minuten nicht
        // verfallen und er sie weiter verbrauchen darf. Der Block erscheint jetzt
        // auch mit Restguthaben; ob Kauf-Knoepfe darin stehen, entscheidet der
        // Server (canBuy aus /stripe/packages).
        const minutesEl = $('#settingsMinutes')[0];
        if (minutesEl) {
            const extraSec = s.chatLiveExtraSeconds || 0;
            const showMinutes = (!!s.chatPlanPaid || extraSec > 0 || !!s.chatCanBuyMinutes) && !s.chatLiveCapUnlimited;
            minutesEl.classList.toggle('hidden', !showMinutes);
            if (showMinutes) {
                const balanceEl = $('#settingsMinutesBalance')[0];
                if (balanceEl) {
                    balanceEl.textContent = (t('minutesBalance') || '')
                        .replace('{min}', String(Math.floor(extraSec / 60)));
                }
                // Pakete einmal je Settings-Aufruf laden. Preise und Verfuegbarkeit
                // haengen am Tarif, deshalb kommen sie vom Server und stehen nicht
                // im Client.
                this._loadMinutePackages();
            }
        }

        // Minuten teilen (2026-08-31): eigener Block, unabhaengig vom
        // Guthaben-Kontingent -- er zeigt Freigaben, nicht Minuten.
        // Nur bei offenem Einstellungen-Tab laden: updateSettingsUI() kommt auch
        // bei jedem 'chat-authed' (Reconnect) und Cap-Push. Beim Oeffnen des
        // Tabs ruft navigation.js updateSettingsUI() ohnehin erneut auf.
        if (!$('#tabSettings')[0]?.classList.contains('hidden')) {
            this._loadLiveShares();
        }

        // Upgrade-Button: versteckt fuer den Top-Tarif. Das ist der Tarif mit
        // eigenem Live-Kontingent (heute Premium) -- darueber gibt es nichts mehr
        // zu buchen. Zusatzminuten sind kein Upgrade, die laufen ueber die Pakete.
        const upgradeBtn = $('#btnPlanUpgrade')[0];
        if (upgradeBtn) {
            upgradeBtn.classList.toggle('hidden', !!s.chatPlanLiveQuota);
        }

        const langSelect = $('#settingsLangSelect')[0];
        if (langSelect) {
            if (langSelect.options.length === 0) {
                for (const code of AVAILABLE_LANGS) {
                    const opt = document.createElement('option');
                    opt.value = code;
                    opt.textContent = LANG_NAMES[code] || code.toUpperCase();
                    langSelect.appendChild(opt);
                }
            }
            langSelect.value = VCS.state.currentLang;
        }

        // Tastatureinstellung
        const sendKeyGroup = $('#sendKeyRadioGroup')[0];
        if (sendKeyGroup) {
            const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
            const labelEnter = $('#sendKeyLabelEnter')[0];
            const labelNewline = $('#sendKeyLabelNewline')[0];
            if (labelEnter) labelEnter.textContent = isMobile ? t('sendKeyEnterMobile') : t('sendKeyEnterDesktop');
            if (labelNewline) labelNewline.textContent = isMobile ? t('sendKeyNewlineMobile') : t('sendKeyNewlineDesktop');
            const current = localStorage.getItem('chat-send-key') || 'newline';
            const radio = sendKeyGroup.querySelector(`input[value="${current}"]`);
            if (radio) radio.checked = true;
        }

        $('#btnUnregister')[0]?.classList.toggle('hidden', !VCS.state.chatRegistered);
        $('#btnDeleteAccount')[0]?.classList.toggle('hidden', !VCS.state.chatRegistered);
        VCS.e2e?.updateSecurityPasswordStatus?.();
    }

    async loadChats() {
        // Approval-Liste parallel anstossen, damit der Filter in renderChatList()
        // entfernte Kontakte ausblendet, sobald die Daten da sind.
        if (!VCS.state.chatContactsLoaded) {
            void VCS.chatContacts?.loadContacts?.();
        }
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/chats', {
                headers: VCS.chat?.authHeaders()
            });
            if (!res.ok) {
                return;
            }
            VCS.state.chatChats = await res.json();

            let totalUnread = 0;
            VCS.state.chatUnreadCounts = {};
            VCS.state.chatChats.forEach(c => {
                if (c.unreadCount > 0) {
                    VCS.state.chatUnreadCounts[c.id] = c.unreadCount;
                    totalUnread += c.unreadCount;
                }
                if (c.e2eEnabled) {
                    VCS.state.chatE2EEnabled[c.id] = true;
                }
                // Gruppen: Name direkt setzen
                if (c.isGroup) {
                    c._peerName = c.name;
                    c._peerAvatarHash = c.avatarHash || null;
                    c._isGroup = true;
                }
                // Dating-Chat (Phase 5): Peer ist ein @handle (Regel 5). Name liefert der
                // Server — /contacts/sync kennt den handle nicht. Foto-im-Chat spaeter
                // (Dating-Fotos brauchen Auth-Header), vorerst die Initiale.
                if (c.isDating) {
                    c._peerName = c.peerName || VCS.chat?.peerPhone?.(c);
                    c._peerAvatarHash = null;
                    c._isDating = true;
                }
            });
            this._updateChatUnreadBadge();
            VCS.notify?.applyBadge?.(totalUnread);
            // E2E-Status ist jetzt bekannt -- offenes Anruf-Overlay nachziehen.
            VCS.chatContacts?.refreshIncomingCallOptIn?.();

            // Dating-Peers (@handle) NICHT an /contacts/sync — sie sind keine Kontakte, der
            // Server kennt sie dort nicht, und Name/Foto stehen schon oben (Regel 5).
            const peerPhones = VCS.state.chatChats.filter(c => !c.isGroup && !c.isDating).map(c => VCS.chat?.peerPhone?.(c));
            if (peerPhones.length > 0) {
                const syncRes = await fetch(VCS.Chat.API_BASE + '/contacts/sync', {
                    method : 'POST',
                    headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                    body   : JSON.stringify({phones: peerPhones})
                });
                if (syncRes.ok) {
                    const data = await syncRes.json();
                    const infoMap = Object.fromEntries((data.registered || []).map(r => [r.phone, r]));
                    VCS.state.chatChats.filter(c => !c.isGroup && !c.isDating).forEach(c => {
                        const info = infoMap[VCS.chat?.peerPhone?.(c)];
                        // Dating-Match: die Nummer nie als Namens-Fallback -- sie darf in
                        // der UI gar nicht erscheinen (Kontakt entstand aus dem Swipe).
                        const fallbackName = c.isDatingMatch
                            ? t('datingContactMatch')
                            : VCS.chat?.peerPhone?.(c);
                        c._peerName = info?.displayName || fallbackName;
                        c._peerAvatarHash = info?.avatarHash || c.peerAvatarHash || null;
                    });
                }
            }

            // Bot-Chat sicherstellen
            this._ensureBotChat();

            VCS.chatMessages?.renderChatList?.();
            VCS.e2e?.decryptPreviews?.();
            VCS.chatPresence?.fetchPresence?.();
            VCS.chatContacts?.loadPendingCount?.();
        } catch (e) {
            console.error('[chat] loadChats error:', e);
        }
    }

    openConversation(chatId, peerPhone, peerNameArg) {
        let chat = VCS.state.chatChats.find(c => c.id === chatId);
        if (!chat && peerPhone) {
            chat = {
                id          : chatId,
                participant1: VCS.state.chatMyPhone,
                participant2: peerPhone,
                _peerName   : peerNameArg || peerPhone
            };
            VCS.state.chatChats.push(chat);
        }
        if (!chat) {
            return;
        }

        const isGroup = chat._isGroup || chat.isGroup || false;
        VCS.state.chatActiveChatId = chatId;
        VCS.state.chatActiveIsGroup = isGroup;

        // Service Worker: Nachrichten-Stapel dieses Chats leeren und seine
        // Benachrichtigung schliessen (sw.js "chat-opened").
        navigator.serviceWorker?.controller?.postMessage({ type: 'chat-opened', chatId });
        // Server: fuer diesen Chat keinen Web-Push an dieses Geraet.
        VCS.signaling?.sendActiveChat?.();

        // Dating-Match: kein Nummern-Fallback im Header (Nummer nie sichtbar).
        const headerFallback = isGroup
            ? chat.name
            : (chat.isDatingMatch ? t('datingContactMatch') : VCS.chat?.peerPhone?.(chat));
        const peerName = peerNameArg || chat._peerName || headerFallback;
        const nameEl = $('#chatDetailPeerName')[0];
        nameEl.textContent = peerName;
        $('#chatDetailPeerPhone')[0].textContent = '';

        // Klick auf Gruppenname → Gruppen-Detail (stopPropagation verhindert
        // dass der .chat-header-info Handler auch die Media-Galerie öffnet)
        if (isGroup) {
            nameEl.style.cursor = 'pointer';
            nameEl.onclick = (e) => {
                e.stopPropagation();
                VCS.chatGroups?.openGroupDetail?.(chatId);
            };
        } else {
            nameEl.style.cursor = '';
            nameEl.onclick = null;
        }

        // Gruppen-Mitglieder-Leiste
        const membersBar = $('#chatGroupMembersBar')[0];
        if (membersBar) {
            if (isGroup) {
                membersBar.classList.remove('hidden');
                this._loadGroupMembersBar(chatId);
            } else {
                membersBar.classList.add('hidden');
                membersBar.innerHTML = '';
            }
        }

        // Bot-Chat: Header-Buttons ausblenden (auch das 3-Punkte-Menü,
        // da dort nur Call-Actions enthalten sind, die Bots nicht haben).
        // Footer: Attachment-Button ebenfalls aus -- Bot akzeptiert nur Text + Voice (STT).
        // chatMicBtn bleibt bewusst sichtbar, damit der User den Bot per Sprache ansprechen kann.
        const isBot = VCS.Chat.isBotChat(chatId);
        $('#chatTranslateToggle')[0]?.classList.toggle('hidden', isBot);
        $('#chatCallAudioBtn')[0]?.classList.toggle('hidden', isBot);
        $('#chatCallVideoBtn')[0]?.classList.toggle('hidden', isBot);
        $('#chatHeaderMenuBtn')[0]?.classList.toggle('hidden', isBot);
        $('#chatAttachBtn')[0]?.classList.toggle('hidden', isBot);

        // Dating-Chat (Phase 5): Medien laufen noch nicht ueber den Alias-Pfad (nur Text)
        // -> Anhang-Button aus. Die Call-Buttons bleiben — der Video-Anruf ist der Sinn.
        const isDatingChat = chat._isDating || (peerPhone && peerPhone[0] === '@')
            || ((VCS.chat?.peerPhone?.(chat) || '')[0] === '@');
        if (isDatingChat) {
            $('#chatAttachBtn')[0]?.classList.add('hidden');
        }

        VCS.chatMessages?.clearReply?.();
        VCS.state.chatEditingMessageId = null;
        VCS.state.chatScrollFabNewCount = 0;
        $('#chatScrollFab')[0]?.classList.add('hidden');
        $('#chatTypingIndicator')[0]?.classList.add('hidden');
        $('#chatRecordingIndicator')[0]?.classList.add('hidden');

        if (VCS.nav?.isDesktopLayout()) {
            VCS.nav?.desktopHideEmptyState();
            $('#chatDetailScreen')[0]?.classList.remove('hidden');
            document.querySelectorAll('.chat-list-item').forEach(el => {
                el.classList.toggle('chat-list-item--active', el.dataset.chatId === chatId);
            });
        } else {
            document.querySelectorAll('#appShell > .app-tab').forEach(t => t.classList.add('hidden'));
            $('#chatDetailScreen')[0]?.classList.remove('hidden');
        }

        VCS.chatMessages?.loadMessages?.(chatId);
        // Gespeicherten Entwurf (falls vorhanden) ins Eingabefeld laden und
        // Hoehe/Buttons/Cursor wiederherstellen.
        VCS.chatMessages?.restoreDraft?.(chatId);
        VCS.chatPresence?.updateHeaderStatus?.();
        if (!isGroup) {
            VCS.chatPresence?.fetchPresence?.();
        }
        VCS.e2e?.updateTranslateToggle?.();

        if (VCS.state.chatE2EEnabled[chatId]) {
            VCS.e2e?.getOrDeriveKey?.(chatId).catch(() => {});
        }

        const avatarEl = $('#chatDetailAvatar')[0];
        if (avatarEl) {
            if (isGroup) {
                const avatarHash = chat._peerAvatarHash || chat.avatarHash || null;
                if (avatarHash) {
                    avatarEl.textContent = '';
                    avatarEl.classList.add('has-avatar');
                    avatarEl.innerHTML = `<img src="${VCS.Chat.API_BASE}/groups/${chatId}/avatar/${avatarHash}" alt="">`;
                } else {
                    avatarEl.classList.remove('has-avatar');
                    avatarEl.innerHTML = '';
                    avatarEl.textContent = peerName[0]?.toUpperCase() || '?';
                }
                avatarEl.style.cursor = '';
                avatarEl.onclick = null;
            } else {
                const peerAvatarHash = chat._peerAvatarHash || null;
                if (peerAvatarHash) {
                    avatarEl.textContent = '';
                    avatarEl.classList.add('has-avatar');
                    avatarEl.style.cursor = 'pointer';
                    avatarEl.innerHTML = `<img src="${VCS.Chat.API_BASE}/profile/avatar/${peerAvatarHash}" alt="" class="chat-detail-avatar-img">`;
                    avatarEl.onclick = () => VCS.chat?.openAvatarViewer?.(peerName, peerAvatarHash);
                } else {
                    avatarEl.classList.remove('has-avatar');
                    avatarEl.style.cursor = '';
                    avatarEl.onclick = null;
                    avatarEl.innerHTML = '';
                    avatarEl.textContent = peerName[0]?.toUpperCase() || '?';
                }
            }
        }

        // Call-Buttons: 1:1 vs Group vs Bot
        const audioBtn = $('#chatCallAudioBtn')[0];
        const videoBtn = $('#chatCallVideoBtn')[0];
        const groupCallBtn = $('#chatGroupCallBtn')[0];
        if (isBot) {
            if (audioBtn) audioBtn.classList.add('hidden');
            if (videoBtn) videoBtn.classList.add('hidden');
            if (groupCallBtn) groupCallBtn.classList.add('hidden');
        } else if (isGroup) {
            if (audioBtn) audioBtn.classList.add('hidden');
            if (videoBtn) videoBtn.classList.add('hidden');
            if (groupCallBtn) groupCallBtn.classList.remove('hidden');
        } else {
            if (audioBtn) audioBtn.classList.remove('hidden');
            if (videoBtn) videoBtn.classList.remove('hidden');
            if (groupCallBtn) groupCallBtn.classList.add('hidden');
        }

        // Minuten teilen (2026-08-31): nur echte 1:1-Kontakte -- kein Bot
        // (kein Live-Feature), keine Gruppe (Abschnitt 9, aussen vor), kein
        // Dating-Match (peerPhone ist dort ein "@handle"-Alias, kein echter
        // Nutzer, mit dem der Server etwas anfangen koennte). Zusaetzlich nur
        // bei planPaid -- Free darf teilen nicht (Festlegung 29.08.).
        const shareBtn = $('#chatShareMinutesBtn')[0];
        if (shareBtn) {
            // Wer Guthaben hat, darf teilen -- unabhaengig vom Tarif (Korrektur
            // 31.08., siehe routes.js POST /minutes/share).
            const ownerCanShare = !!VCS.state.chatPlanPaid || (VCS.state.chatLiveExtraSeconds || 0) > 0;
            const canShare = !isBot && !isGroup && !isDatingChat && ownerCanShare;
            shareBtn.classList.toggle('hidden', !canShare);
            shareBtn.classList.remove('chat-header-menu-item--active');
            if (canShare && peerPhone) {
                this._syncShareMinutesButton(peerPhone);
            }
        }

        // Banner: aktiver Group-Call?
        const banner = $('#groupCallBanner')[0];
        if (banner) banner.classList.add('hidden');
        if (isGroup) {
            fetch(VCS.Chat.API_BASE + '/call/group/' + chatId + '/active', {
                headers: { 'x-phone': VCS.state.chatMyPhone }
            }).then(r => r.json()).then(data => {
                if (data.active && banner) {
                    banner.classList.remove('hidden');
                    banner.dataset.callId = data.callId;
                    banner.dataset.groupId = chatId;
                }
            }).catch(() => {});
        }
    }

    async _loadGroupMembersBar(groupId) {
        const bar = $('#chatGroupMembersBar')[0];
        if (!bar) return;
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/groups/' + groupId, {
                headers: VCS.chat?.authHeaders()
            });
            if (!res.ok) return;
            const data = await res.json();
            const count = (data.members || []).length;
            // Parity mit Flutter: nur Anzahl, keine Namen-Chips.
            bar.textContent = count + ' ' + t('groupMembers');
        } catch (e) {
            console.error('[chat] loadGroupMembersBar error:', e);
        }
    }

    // -- Minuten teilen (2026-08-31) --------------------------------------

    // Haken im Chat-Header-Menue, wenn mit diesem Kontakt schon geteilt wird.
    // Fire-and-forget beim Oeffnen des Chats -- ein Fehler soll den
    // Chat-Aufbau nicht aufhalten, der Menuepunkt bleibt dann ohne Haken
    // (der Klick-Handler in chat-messages.js prueft beim Oeffnen des Menues
    // ohnehin frisch nach, bevor er den Toggle-Dialog zeigt).
    async _syncShareMinutesButton(peerPhone) {
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/minutes/shares', {
                headers: VCS.chat?.authHeaders()
            });
            if (!res.ok) return;
            const data = await res.json();
            const isSharing = (data.shares || []).some(s => s.beneficiaryPhone === peerPhone);
            $('#chatShareMinutesBtn')[0]?.classList.toggle('chat-header-menu-item--active', isSharing);
        } catch (e) {
            console.warn('[chat] Minuten-Teilen-Status nicht ladbar:', e.message);
        }
    }

    // Einstellungen "Geteilte Minuten": Uebersicht + Beenden-Knopf pro Zeile.
    // Sichtbar nur bei planPaid, wie das Teilen selbst (Festlegung 29.08.).
    async _loadLiveShares() {
        const block = $('#settingsLiveShares')[0];
        const list = $('#settingsLiveSharesList')[0];
        if (!block || !list) return;
        if (!VCS.state.chatPlanPaid) {
            block.classList.add('hidden');
            return;
        }
        block.classList.remove('hidden');
        let shares = [];
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/minutes/shares', {
                headers: VCS.chat?.authHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                shares = data.shares || [];
            }
        } catch (e) {
            console.warn('[chat] Geteilte-Minuten-Liste nicht ladbar:', e.message);
        }
        list.innerHTML = '';
        if (!shares.length) {
            const empty = document.createElement('div');
            empty.className = 'settings-share-row-used';
            empty.textContent = t('liveShareSettingsEmpty');
            list.appendChild(empty);
            return;
        }
        shares.forEach((share) => {
            const row = document.createElement('div');
            row.className = 'settings-share-row';

            const info = document.createElement('div');
            info.className = 'settings-share-row-info';
            const name = document.createElement('span');
            name.className = 'settings-share-row-name';
            name.textContent = share.displayName || share.beneficiaryPhone;
            const used = document.createElement('span');
            used.className = 'settings-share-row-used';
            used.textContent = t('liveShareSettingsUsed').replace('{used}', String(Math.floor((share.secondsUsed || 0) / 60)));
            info.appendChild(name);
            info.appendChild(used);

            const stopBtn = document.createElement('button');
            stopBtn.type = 'button';
            stopBtn.className = 'settings-share-row-stop';
            stopBtn.textContent = t('liveShareStopButton');
            stopBtn.addEventListener('click', () => VCS.chatContacts?.stopSharingMinutes?.(share.beneficiaryPhone, share.displayName || share.beneficiaryPhone));

            row.appendChild(info);
            row.appendChild(stopBtn);
            list.appendChild(row);
        });
    }

    // ── Bot-Chat sicherstellen ─────────────────────────────

    // -- Minutenkauf (2026-08-30) --------------------------------------
    // Pakete kommen vom Server, weil Preis und Verfuegbarkeit am Tarif haengen:
    // Premium-Kunden zahlen weniger, und wer nicht zahlt, darf gar nicht kaufen.
    // Der Client haelt deshalb keine Preise vor.
    // Nach einem Tarifwechsel neu laden: Premium-Kunden zahlen weniger, sonst
    // stuenden nach dem Upgrade weiter die alten Preise da.
    resetMinutePackages() {
        this._minutePackagesLoaded = false;
        this._minutePackages = null;
        const listEl = $('#settingsMinutesList')[0];
        if (listEl) listEl.textContent = '';
        // Der Nachkauf im Gespraech zeigt dieselben Pakete -- auch dort muss die
        // alte Preisliste weg, sonst steht nach dem Upgrade der alte Preis drin.
        VCS.liveCapNotice?.resetPackages?.();
    }

    // Paketliste holen. Gemeinsame Quelle fuer die Einstellungen und den
    // Nachkauf im Gespraech (live-cap-notice.js) -- beide zeigen dieselben
    // Pakete, also darf es nur eine Abfrage geben.
    // Rueckgabe: Array (auch leer) oder null, wenn die Abfrage scheiterte.
    async fetchMinutePackages() {
        if (this._minutePackages) return this._minutePackages;
        // Laeuft schon eine Abfrage, auf dieselbe warten statt eine zweite zu
        // starten: updateSettingsUI() kommt bei jedem Cap-Push erneut vorbei.
        if (this._minutePackagesPromise) return this._minutePackagesPromise;
        this._minutePackagesPromise = (async () => {
            try {
                const res = await fetch(VCS.Chat.API_BASE + '/stripe/packages', {
                    headers: { 'Authorization': 'Bearer ' + VCS.state.chatAuthToken }
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data.ok) {
                    console.warn('[minutes] Pakete nicht ladbar:', res.status, data.error || '');
                    return null;
                }
                this._minutePackages = data.packages || [];
                // Darf dieser Nutzer ueberhaupt kaufen? Entscheidet der Server
                // (Tarif + Schalter MINUTES_FREE_PURCHASE). Ein Free-Konto mit
                // Restguthaben sieht seinen Stand, aber keine Kauf-Knoepfe.
                this._minutesCanBuy = data.canBuy !== false;
                return this._minutePackages;
            } catch (err) {
                console.warn('[minutes] Pakete nicht ladbar:', err.message);
                return null;
            } finally {
                this._minutePackagesPromise = null;
            }
        })();
        return this._minutePackagesPromise;
    }

    async _loadMinutePackages() {
        // Nur einmal je Sitzung rendern. Ohne die Sperre wuerde jeder Aufruf von
        // updateSettingsUI() -- und der kommt bei jedem Cap-Push -- neu zeichnen.
        if (this._minutePackagesLoaded) return;
        const packages = await this.fetchMinutePackages();
        if (!packages) return;
        this._minutePackagesLoaded = true;

        // Kein Kaufrecht (z.B. Free mit Restguthaben): Liste, Zustimmung und
        // Hinweistext bleiben leer bzw. verborgen. Der Guthabenstand darueber
        // steht weiter da -- er gehoert dem Nutzer.
        const listEl = $('#settingsMinutesList')[0];
        const consentEl = $('#settingsMinutes .settings-minutes-consent')[0];
        const hintEl = $('#settingsMinutesHint')[0];
        if (!this._minutesCanBuy) {
            if (listEl) listEl.textContent = '';
            consentEl?.classList.add('hidden');
            if (hintEl) hintEl.textContent = t('minutesKeepHint') || '';
            return;
        }
        consentEl?.classList.remove('hidden');
        if (hintEl) hintEl.textContent = t('minutesHint') || '';
        this.renderMinutePackages(listEl, packages,
            (pkgId, btn) => this._buyMinutePackage(pkgId, btn));
    }

    // Paket-Knoepfe in eine Liste zeichnen. `onPick(packageId, btn)` bekommt den
    // Klick -- die Einstellungen und der Nachkauf im Gespraech holen ihre
    // Zustimmung an unterschiedlichen Stellen ab.
    renderMinutePackages(listEl, packages, onPick) {
        if (!listEl) return;
        listEl.textContent = '';
        if (!packages.length) {
            // Kein Paket hat eine Stripe-Price-ID -- im Admin nachtragen.
            const hint = document.createElement('span');
            hint.className = 'settings-pro-trial-label';
            hint.textContent = t('minutesUnavailable') || '';
            listEl.appendChild(hint);
            return;
        }
        packages.forEach((pkg) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'settings-minutes-btn';
            btn.dataset.packageId = pkg.id;

            const title = document.createElement('span');
            title.textContent = (t('minutesPackage') || '{min} Min').replace('{min}', String(pkg.minutes));

            const priceRow = document.createElement('span');
            const price = document.createElement('span');
            price.className = 'settings-minutes-btn-price';
            price.textContent = (pkg.priceCents / 100).toFixed(2).replace('.', ',') + ' €';
            priceRow.appendChild(price);
            // Beim Rabatt den Normalpreis durchgestrichen daneben: der Vorteil
            // wirkt nur, wenn sichtbar ist, wovon er abgeht.
            if (pkg.discounted && pkg.regularPriceCents > pkg.priceCents) {
                const was = document.createElement('span');
                was.className = 'settings-minutes-btn-was';
                was.textContent = (pkg.regularPriceCents / 100).toFixed(2).replace('.', ',') + ' €';
                priceRow.appendChild(was);
            }

            btn.appendChild(title);
            btn.appendChild(priceRow);
            btn.addEventListener('click', () => onPick(pkg.id, btn));
            listEl.appendChild(btn);
        });
    }

    async _buyMinutePackage(packageId, btn) {
        const consentEl = $('#settingsMinutesConsent')[0];
        // Ohne ausdrueckliche Zustimmung zum sofortigen Leistungsbeginn kein
        // kostenpflichtiger Checkout (Paragraf 356 BGB). Der Server prueft das
        // ebenfalls -- hier nur, damit der Nutzer nicht erst bei Stripe scheitert.
        if (!consentEl || !consentEl.checked) {
            VCS.ui?.showToast?.(t('minutesConsentRequired') || '', 'warning', 6000);
            consentEl?.focus();
            return;
        }
        const consentText = $('#settingsMinutes .settings-minutes-consent span')[0]?.textContent || '';
        btn.disabled = true;
        try {
            await this.startPackageCheckout(packageId, consentText);
        } finally {
            btn.disabled = false;
        }
    }

    // Checkout starten. Der genaue Wortlaut der Zustimmung wird mitgeschickt und
    // serverseitig protokolliert -- deshalb kommt er von der Stelle, an der der
    // Nutzer ihn wirklich gelesen hat, und ist kein fester Text hier.
    // Rueckgabe: true, wenn Stripe geoeffnet wurde.
    async startPackageCheckout(packageId, consentText) {
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/stripe/create-package-checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + VCS.state.chatAuthToken
                },
                body: JSON.stringify({
                    packageId,
                    revocationConsent: true,
                    revocationConsentText: consentText || '',
                    // Wohin Stripe zurueckschickt. Der Messenger laeuft je nach
                    // Auslieferung unter '/' (eigene Subdomain, site.webmanifest
                    // scope) oder unter '/messenger/'. Der Server nimmt den Wert
                    // nur aus einer festen Liste an.
                    returnTo: location.pathname.startsWith('/messenger/') ? '/messenger/' : '/'
                })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.url) {
                VCS.ui?.showToast?.(data.error || t('minutesCheckoutFailed') || '', 'error', 8000);
                return false;
            }
            // Stripe in einem eigenen Tab: die laufende Sitzung -- und vor allem
            // der laufende Anruf -- darf durch den Kauf nicht abreissen.
            window.open(data.url, '_blank', 'noopener');
            return true;
        } catch (err) {
            console.warn('[minutes] Checkout fehlgeschlagen:', err.message);
            VCS.ui?.showToast?.(t('minutesCheckoutFailed') || '', 'error', 8000);
            return false;
        }
    }

    _ensureBotChat() {
        if (!VCS.state.chatMyPhone || !VCS.state.chatRegistered) return;
        const botPhone = VCS.Chat.BOT_PHONE;
        const sorted = [VCS.state.chatMyPhone, botPhone].sort();
        const botChatId = sorted[0] + '_' + sorted[1];

        // Bot-Chat nur mit Concierge-Recht (2026-10-15, users.allow_concierge),
        // unabhaengig vom Tarif. Ohne Recht einen bestehenden Bot-Chat aus der
        // Liste entfernen (Entzug im Admin). Der Server prueft dasselbe noch
        // einmal (routes.js mayUseConcierge), das hier steuert nur die Liste.
        if (!VCS.state.conciergeAvailable) {
            VCS.state.chatChats = VCS.state.chatChats.filter(c => c.id !== botChatId);
            return;
        }

        const exists = VCS.state.chatChats.some(c => c.id === botChatId);
        if (!exists) {
            VCS.state.chatChats.unshift({
                id              : botChatId,
                participant1    : sorted[0],
                participant2    : sorted[1],
                lastMessageText : null,
                lastMessageAt   : null,
                lastMessageSender: null,
                lastMessageStatus: null,
                unreadCount     : 0,
                e2eEnabled      : false,
                _peerName       : VCS.Chat.BOT_NAME,
                _peerAvatarHash : VCS.Chat.BOT_AVATAR,
                _isBot          : true
            });
        } else {
            // Bestehenden Bot-Chat markieren
            const chat = VCS.state.chatChats.find(c => c.id === botChatId);
            if (chat) {
                chat._peerName = VCS.Chat.BOT_NAME;
                chat._peerAvatarHash = 'bc97fde7785bd1ba';
                chat._isBot = true;
            }
        }
    }

    // ── Password-Setup Overlay (Neu-Registrierung) ────────

    _checkPhoneExists(phone) {
        return new Promise((resolve) => {
            const ws = VCS.state.ws;
            if (!ws || ws.readyState !== WebSocket.OPEN) {
                console.warn('[chat] WS nicht offen fuer check-phone');
                resolve(false);
                return;
            }

            const timeout = setTimeout(() => {
                ws.removeEventListener('message', handler);
                console.warn('[chat] check-phone timeout');
                resolve(false);
            }, 5000);

            const handler = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === 'check-phone-result' && msg.phone === phone) {
                        clearTimeout(timeout);
                        ws.removeEventListener('message', handler);
                        resolve(!!msg.exists);
                    }
                } catch (_) {}
            };

            ws.addEventListener('message', handler);
            VCS.signaling?.send({ type: 'check-phone', phone });
        });
    }

    // Bindet einen Auge-Toggle-Button an ein Passwort-Feld + SVG.
    // Tauscht type=password<->text und das offene/geschlossene Auge-Icon.
    _bindPwToggle(btnSel, inputSel, eyeSel) {
        const btn = $(btnSel)[0];
        const input = $(inputSel)[0];
        const eye = $(eyeSel)[0];
        if (!btn || !input || !eye) { return; }
        const eyeOpen = '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>';
        const eyeClosed = '<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>';
        btn.addEventListener('click', () => {
            const isPassword = input.type === 'password';
            input.type = isPassword ? 'text' : 'password';
            eye.innerHTML = isPassword ? eyeClosed : eyeOpen;
            btn.setAttribute('aria-label',
                isPassword ? (t('loginPasswordHide') || 'Passwort verbergen')
                           : (t('loginPasswordShow') || 'Passwort anzeigen'));
        });
    }

    showPasswordSetupDialog() {
        return new Promise((resolve) => {
            const overlay = $('#passwordSetupOverlay')[0];
            const input   = $('#pwSetupInput')[0];
            const confirm = $('#pwSetupConfirmInput')[0];
            const error   = $('#pwSetupError')[0];
            const submit  = $('#pwSetupSubmit')[0];

            if (!overlay || !input || !confirm) {
                resolve(null);
                return;
            }

            input.value   = '';
            confirm.value = '';
            if (error) {
                error.classList.add('hidden');
                error.textContent = '';
            }
            overlay.classList.remove('hidden');
            input.focus();

            const cleanup = (pw) => {
                overlay.classList.add('hidden');
                submit.removeEventListener('click', onSubmit);
                input.removeEventListener('keydown', onKeydown);
                confirm.removeEventListener('keydown', onKeydown);
                resolve(pw);
            };

            const onSubmit = () => {
                const pw  = input.value.trim();
                const pw2 = confirm.value.trim();

                if (pw.length < 6) {
                    error.textContent = t('securityPasswordTooShort');
                    error.classList.remove('hidden');
                    input.focus();
                    return;
                }
                if (pw !== pw2) {
                    error.textContent = t('passwordMismatch');
                    error.classList.remove('hidden');
                    confirm.focus();
                    return;
                }

                error.classList.add('hidden');
                cleanup(pw);
            };

            const onKeydown = (e) => {
                if (e.key === 'Enter') {
                    onSubmit();
                }
            };

            submit.addEventListener('click', onSubmit);
            input.addEventListener('keydown', onKeydown);
            confirm.addEventListener('keydown', onKeydown);
        });
    }

    // ── Init ─────────────────────────────────────────────────

    // Deep-Link ?login=1 -> gleich die Anmelde-Karte zeigen statt der
    // Registrierung. Kommt von der Einladungsseite (Button "Ich habe schon ein
    // Konto"), taugt aber fuer jeden Link, der direkt zur Anmeldung fuehren soll.
    applyAuthDeepLink() {
        let wantsLogin = false;
        try {
            wantsLogin = new URLSearchParams(window.location.search).get('login') === '1';
        } catch (_) {
            return;
        }
        if (!wantsLogin) {
            return;
        }

        // Auf einem Geraet mit aktivem Konto gibt es keine Reg-Ansicht -- dann
        // waere das Umschalten wirkungslos bis irrefuehrend.
        if (VCS.state.chatRegistered) {
            this._stripLoginParam();
            return;
        }

        $('#regCardNew')[0]?.classList.add('hidden');
        $('#regCardLogin')[0]?.classList.remove('hidden');
        $('#loginEmailInput')[0]?.focus();
        this._stripLoginParam();
    }

    // Parameter aus der Adresszeile nehmen: nach dem Umschalten hat er seinen
    // Zweck erfuellt, und ein Reload soll den Nutzer nicht erneut wegschubsen,
    // wenn er inzwischen selbst auf Registrieren gewechselt hat.
    _stripLoginParam() {
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('login');
            history.replaceState(null, '', url.pathname + url.search + url.hash);
        } catch (_) {}
    }

    init() {
        $('#btnPhoneRegister')[0]?.addEventListener('click', () => this.registerPhone());
        $('#phoneInput')[0]?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                this.registerPhone();
            }
        });
        // E-Mail ist seit Auth-Hardening 2026-05-02 fuer alle Pflicht. Hint nur fuer +7.
        $('#phoneInput')[0]?.addEventListener('input', () => {
            const val = ($('#phoneInput')[0]?.value || '').replace(/[^+\d]/g, '');
            $('#regEmailHint')[0]?.classList.toggle('hidden', !val.startsWith('+7'));
        });

        // Login-Card (Card B) Wiring
        $('#btnLoginEmail')[0]?.addEventListener('click', () => this.loginWithEmail());
        $('#loginPasswordInput')[0]?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { this.loginWithEmail(); }
        });
        // Auge-Toggle fuer PW-Sichtbarkeit (Login + Setup-Dialog).
        this._bindPwToggle('#btnLoginPwToggle', '#loginPasswordInput', '#loginPwEyeIcon');
        this._bindPwToggle('#btnPwSetupToggle', '#pwSetupInput', '#pwSetupEyeIcon');
        this._bindPwToggle('#btnPwSetupConfirmToggle', '#pwSetupConfirmInput', '#pwSetupConfirmEyeIcon');
        // Toggle Reg <-> Login
        $('#btnSwitchToLogin')[0]?.addEventListener('click', () => {
            $('#regCardNew')[0]?.classList.add('hidden');
            $('#regCardLogin')[0]?.classList.remove('hidden');
        });
        $('#btnSwitchToReg')[0]?.addEventListener('click', () => {
            $('#regCardLogin')[0]?.classList.add('hidden');
            $('#regCardNew')[0]?.classList.remove('hidden');
        });
        // Direkt nach dem Wiring: ein ?login=1 in der URL soll dieselbe
        // Umschaltung ausloesen, als haette der Nutzer den Link geklickt.
        this.applyAuthDeepLink();
        $('#btnVerifySms')[0]?.addEventListener('click', () => this.verifySmsCode());
        $('#btnResendSms')[0]?.addEventListener('click', () => this.resendSmsCode());
        const otpCells = document.querySelectorAll('.reg-otp-cell');
        const syncOtp = () => {
            const code = Array.from(otpCells).map((el) => el.value || '').join('');
            const hidden = $('#smsCodeInput')[0];
            if (hidden) {
                hidden.value = code;
            }
            return code;
        };
        otpCells.forEach((cell, idx) => {
            cell.addEventListener('input', () => {
                cell.value = (cell.value || '').replace(/\D/g, '').slice(-1);
                const code = syncOtp();
                if (cell.value && otpCells[idx + 1]) {
                    otpCells[idx + 1].focus();
                    otpCells[idx + 1].select?.();
                }
                if (code.length === 6) {
                    this.verifySmsCode();
                }
            });
            cell.addEventListener('keydown', (e) => {
                if (e.key === 'Backspace') {
                    if (!cell.value && otpCells[idx - 1]) {
                        otpCells[idx - 1].focus();
                        otpCells[idx - 1].value = '';
                        syncOtp();
                        e.preventDefault();
                    }
                } else if (e.key === 'ArrowLeft' && otpCells[idx - 1]) {
                    otpCells[idx - 1].focus();
                    e.preventDefault();
                } else if (e.key === 'ArrowRight' && otpCells[idx + 1]) {
                    otpCells[idx + 1].focus();
                    e.preventDefault();
                } else if (e.key === 'Enter') {
                    this.verifySmsCode();
                    e.preventDefault();
                }
            });
            cell.addEventListener('paste', (e) => {
                const text = (e.clipboardData || window.clipboardData)?.getData('text') || '';
                const digits = text.replace(/\D/g, '').slice(0, 6);
                if (!digits) {
                    return;
                }
                e.preventDefault();
                otpCells.forEach((el, i) => {
                    el.value = digits[i] || '';
                });
                const code = syncOtp();
                const lastIdx = Math.min(digits.length, otpCells.length) - 1;
                otpCells[Math.max(0, lastIdx)]?.focus();
                if (code.length === 6) {
                    this.verifySmsCode();
                }
            });
            cell.addEventListener('focus', () => {
                cell.select?.();
            });
        });
        $('#sendKeyRadioGroup')[0]?.addEventListener('change', (e) => {
            if (e.target.name === 'sendKey') {
                localStorage.setItem('chat-send-key', e.target.value);
            }
        });
        $('#btnChatUnregister')[0]?.addEventListener('click', () => this.unregister());
        $('#btnDeleteAccount')[0]?.addEventListener('click', () => this.deleteAccount());

        // Chat header 3-dot menu (contains audio/video/group-call actions).
        const menuBtn = $('#chatHeaderMenuBtn')[0];
        const menuEl = $('#chatHeaderMenu')[0];
        if (menuBtn && menuEl) {
            menuBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                menuEl.classList.toggle('hidden');
            });
            // Clicking a menu item closes the dropdown (the item's own handler
            // still fires via normal event propagation).
            menuEl.addEventListener('click', () => menuEl.classList.add('hidden'));
            document.addEventListener('click', (e) => {
                if (!menuEl.classList.contains('hidden') &&
                    !menuEl.contains(e.target) &&
                    e.target !== menuBtn &&
                    !menuBtn.contains(e.target)) {
                    menuEl.classList.add('hidden');
                }
            });
        }

        // Avatar Viewer
        $('#avatarViewerOverlay')[0]?.addEventListener('click', () => VCS.chat?.closeAvatarViewer?.());

        // Profil
        $('#btnOpenProfile')[0]?.addEventListener('click', () => this.openProfile());
        $('#btnProfileBack')[0]?.addEventListener('click', () => this.closeProfile());
        $('#btnProfileCamera')[0]?.addEventListener('click', () => this.openProfileCamera());
        $('#btnProfileUpload')[0]?.addEventListener('click', () => $('#profileAvatarInput')[0]?.click());
        $('#profileAvatarWrap')[0]?.addEventListener('click', () => $('#profileAvatarInput')[0]?.click());
        $('#profileAvatarInput')[0]?.addEventListener('change', (e) => {
            const file = e.target.files?.[0];
            if (file) this.uploadAvatar(file);
            e.target.value = '';
        });
        $('#btnRemoveAvatar')[0]?.addEventListener('click', () => this.removeAvatar());

        // Kamera-Overlay
        $('#btnCameraClose')[0]?.addEventListener('click', () => this.closeProfileCamera());
        $('#btnCameraShutter')[0]?.addEventListener('click', () => this.takeProfilePhoto());
        $('#btnCameraFlip')[0]?.addEventListener('click', () => this.flipProfileCamera());

        // Privacy-Accordion
        $('#privacyAccordionToggle')[0]?.addEventListener('click', () => {
            const section = $('#privacyAccordionToggle')[0]?.closest('.settings-accordion');
            const body = $('#privacyAccordionBody')[0];
            if (section && body) {
                section.classList.toggle('open');
                body.classList.toggle('hidden');
            }
        });

        // Privacy-Settings
        ['privacyLastSeen', 'privacyOnline', 'privacyAvatar', 'privacyReadReceipts'].forEach(id => {
            $(`#${id}`)[0]?.addEventListener('change', () => this._savePrivacySettings());
        });
        this._loadPrivacySettings();

        // System Info
        $('#btnSystemInfo')[0]?.addEventListener('click', () => this._openSystemInfo());
        $('#btnCloseSystemInfo')[0]?.addEventListener('click', () => this._closeSystemInfo());
        $('#systemInfoOverlay')[0]?.addEventListener('click', (e) => {
            if (e.target === e.currentTarget) this._closeSystemInfo();
        });
    }

    async _loadPrivacySettings() {
        if (!VCS.state.chatMyPhone) return;
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/privacy-settings', {
                headers: VCS.chat?.authHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                ['privacyLastSeen', 'privacyOnline', 'privacyAvatar', 'privacyReadReceipts'].forEach(id => {
                    const el = $(`#${id}`)[0];
                    if (el) el.value = data[id] || 'everyone';
                });
            }
        } catch (e) {
            console.error('[chat] load privacy settings error:', e);
        }
    }

    async _savePrivacySettings() {
        if (!VCS.state.chatMyPhone) return;
        const body = {
            privacyLastSeen: $('#privacyLastSeen')[0]?.value || 'everyone',
            privacyOnline: $('#privacyOnline')[0]?.value || 'everyone',
            privacyAvatar: $('#privacyAvatar')[0]?.value || 'everyone',
            privacyReadReceipts: $('#privacyReadReceipts')[0]?.value || 'everyone'
        };
        try {
            await fetch(VCS.Chat.API_BASE + '/privacy-settings', {
                method: 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body: JSON.stringify(body)
            });
        } catch (e) {
            console.error('[chat] save privacy settings error:', e);
        }
    }

    // -- System Info -------------------------------------------

    _openSystemInfo() {
        const t = (k) => VCS.i18n?.t(k) || k;
        const overlay = $('#systemInfoOverlay')[0];
        if (!overlay) return;

        // Browser-Name ermitteln
        const ua = navigator.userAgent;
        let browser = 'Unknown';
        if (ua.includes('Firefox/')) browser = 'Firefox';
        else if (ua.includes('Edg/')) browser = 'Edge';
        else if (ua.includes('OPR/') || ua.includes('Opera')) browser = 'Opera';
        else if (ua.includes('Chrome/')) browser = 'Chrome';
        else if (ua.includes('Safari/')) browser = 'Safari';
        const verMatch = ua.match(new RegExp(browser === 'Edge' ? 'Edg/([\\d.]+)' : browser + '/([\\d.]+)'));
        if (verMatch) browser += ' ' + verMatch[1];

        // Werte setzen
        $('#sysInfoBrowser')[0].textContent = browser;
        $('#sysInfoScreen')[0].textContent = `${screen.width} x ${screen.height} (${Math.round(devicePixelRatio * 100)}%)`;
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
        $('#sysInfoMode')[0].textContent = isPWA ? t('systemInfoModePWA') : t('systemInfoModeBrowser');
        let platform = '--';
        const ua2 = navigator.userAgent;
        if (/Android/i.test(ua2)) platform = 'Android';
        else if (/iPhone|iPad|iPod/i.test(ua2)) platform = 'iOS';
        else if (/Mac OS X/i.test(ua2)) platform = 'macOS';
        else if (/Windows/i.test(ua2)) platform = 'Windows';
        else if (/Linux/i.test(ua2)) platform = 'Linux';
        if (/Mobile|Android/i.test(ua2) && !/iPad/i.test(ua2)) platform += ' (Mobile)';
        else if (platform !== '--') platform += ' (Desktop)';
        $('#sysInfoPlatform')[0].textContent = platform;

        // SW-Version aus sw.js auslesen
        const swEl = $('#sysInfoSW')[0];
        swEl.textContent = '--';
        fetch('sw.js?_=' + Date.now())
            .then(r => r.text())
            .then(txt => {
                const m = txt.match(/CACHE_VERSION_STRING\s*=\s*"([^"]+)"/);
                swEl.textContent = m ? m[1] : 'N/A';
            })
            .catch(() => { swEl.textContent = 'N/A'; });

        overlay.classList.remove('hidden');
    }

    _closeSystemInfo() {
        $('#systemInfoOverlay')[0]?.classList.add('hidden');
    }

    // ── Profil ────────────────────────────────────────────────

    updateProfileUI() {
        const name = VCS.state.chatMyName || '';
        const initial = name.charAt(0).toUpperCase() || '?';
        const avatarHash = VCS.state.chatAvatarHash;

        // Settings-Zeile
        $('#settingsProfileName')[0] && ($('#settingsProfileName')[0].textContent = name || '');
        $('#settingsAvatarInitial')[0] && ($('#settingsAvatarInitial')[0].textContent = initial);
        const settingsImg = $('#settingsAvatarImg')[0];
        if (settingsImg) {
            if (avatarHash) {
                settingsImg.src = VCS.Chat.API_BASE + '/profile/avatar/' + avatarHash + '?t=' + Date.now();
                settingsImg.classList.remove('hidden');
            } else {
                settingsImg.classList.add('hidden');
                settingsImg.src = '';
            }
        }

        // Overlay
        $('#profileAvatarLargeInitial')[0] && ($('#profileAvatarLargeInitial')[0].textContent = initial);
        const largeImg = $('#profileAvatarLargeImg')[0];
        if (largeImg) {
            if (avatarHash) {
                largeImg.src = VCS.Chat.API_BASE + '/profile/avatar/' + avatarHash + '?t=' + Date.now();
                largeImg.classList.remove('hidden');
            } else {
                largeImg.classList.add('hidden');
                largeImg.src = '';
            }
        }

        const removeBtn = $('#btnRemoveAvatar')[0];
        if (removeBtn) {
            removeBtn.classList.toggle('hidden', !avatarHash);
        }

        // Profil-Felder: Name + Telefon
        const profileNameInput = $('#profileNameInput')[0];
        if (profileNameInput) {
            profileNameInput.value = name;
        }
        $('#btnProfileSaveName')[0]?.classList.add('hidden');
        const phoneEl = $('#profilePhoneDisplay')[0];
        if (phoneEl) {
            phoneEl.textContent = VCS.state.chatMyPhone || '';
        }
    }

    openProfile() {
        if (!VCS.state.chatRegistered) return;
        this.updateProfileUI();
        const emailEl = $('#profileEmailDisplay')[0];
        if (emailEl) emailEl.textContent = '';
        $('#profileOverlay')[0]?.classList.remove('hidden');
        this._loadProfileEmail();
    }

    async _loadProfileEmail() {
        const el = $('#profileEmailDisplay')[0];
        if (!el || !VCS.state.chatAuthToken) return;
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/auth/me', {
                headers: { 'Authorization': 'Bearer ' + VCS.state.chatAuthToken },
                cache  : 'no-store'
            });
            if (!res.ok) {
                console.warn('[chat] profile email: /auth/me status', res.status);
                el.textContent = '-';
                return;
            }
            const data = await res.json();
            el.textContent = data?.user?.email || '-';
        } catch (e) {
            console.warn('[chat] profile email: network error', e?.message || e);
            el.textContent = '-';
        }
    }

    closeProfile() {
        $('#profileOverlay')[0]?.classList.add('hidden');
    }

    async openProfileCamera() {
        const video = $('#profileCameraVideo')[0];
        if (!video) return;

        this._cameraFacing = 'user';
        try {
            this._cameraStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: { ideal: 512 }, height: { ideal: 512 } },
                audio: false
            });
        } catch (_) {
            try {
                this._cameraStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            } catch (_2) {
                VCS.ui?.showToast?.(t('cameraAccessDenied') || 'Kein Zugriff auf die Kamera', 'error');
                return;
            }
        }

        video.srcObject = this._cameraStream;
        $('#profileCameraOverlay')[0]?.classList.remove('hidden');
    }

    closeProfileCamera() {
        if (this._cameraStream) {
            this._cameraStream.getTracks().forEach(tr => tr.stop());
            this._cameraStream = null;
        }
        const video = $('#profileCameraVideo')[0];
        if (video) video.srcObject = null;
        $('#profileCameraOverlay')[0]?.classList.add('hidden');
    }

    async flipProfileCamera() {
        if (!this._cameraStream) return;
        const video = $('#profileCameraVideo')[0];
        if (!video) return;

        this._cameraFacing = this._cameraFacing === 'user' ? 'environment' : 'user';
        this._cameraStream.getTracks().forEach(tr => tr.stop());

        try {
            this._cameraStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { exact: this._cameraFacing }, width: { ideal: 512 }, height: { ideal: 512 } },
                audio: false
            });
        } catch (_) {
            try {
                this._cameraStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: this._cameraFacing, width: { ideal: 512 }, height: { ideal: 512 } },
                    audio: false
                });
            } catch (_2) {
                // Zurück zur vorherigen Kamera
                this._cameraFacing = this._cameraFacing === 'user' ? 'environment' : 'user';
                this._cameraStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: this._cameraFacing },
                    audio: false
                });
            }
        }
        video.srcObject = this._cameraStream;
    }

    takeProfilePhoto() {
        const video = $('#profileCameraVideo')[0];
        if (!video || !this._cameraStream) return;

        const canvas = document.createElement('canvas');
        const size = Math.min(video.videoWidth, video.videoHeight);
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        // Quadratisch aus der Mitte ausschneiden
        const sx = (video.videoWidth - size) / 2;
        const sy = (video.videoHeight - size) / 2;
        ctx.drawImage(video, sx, sy, size, size, 0, 0, 512, 512);

        canvas.toBlob((blob) => {
            if (blob) {
                this.closeProfileCamera();
                this.uploadAvatar(blob);
            }
        }, 'image/jpeg', 0.85);
    }

    _compressAvatar(file, maxSize = 512, quality = 0.85) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let w = img.width, h = img.height;
                if (w > h) { if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; } }
                else        { if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; } }
                canvas.width = w;
                canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
                URL.revokeObjectURL(img.src);
            };
            img.onerror = () => { URL.revokeObjectURL(img.src); resolve(file); };
            img.src = URL.createObjectURL(file);
        });
    }

    async uploadAvatar(file) {
        if (!VCS.state.chatMyPhone) return;
        const compressed = await this._compressAvatar(file);
        const formData = new FormData();
        formData.append('avatar', compressed, 'avatar.jpg');

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/profile/avatar', {
                method : 'POST',
                headers: VCS.chat?.authHeaders(),
                body   : formData
            });
            const result = await res.json();
            if (result.ok && result.avatarHash) {
                VCS.state.chatAvatarHash = result.avatarHash;
                localStorage.setItem('chat-avatar-hash', result.avatarHash);
                this.updateProfileUI();
                VCS.ui?.showToast?.(t('profilePhotoSaved'), 'success');
            } else {
                VCS.ui?.showToast?.(result.error || 'Upload fehlgeschlagen', 'error');
            }
        } catch (e) {
            console.error('[chat] avatar upload error:', e);
            VCS.ui?.showToast?.('Upload fehlgeschlagen', 'error');
        }
    }

    async removeAvatar() {
        if (!VCS.state.chatMyPhone) return;
        try {
            await fetch(VCS.Chat.API_BASE + '/profile/avatar', {
                method : 'DELETE',
                headers: VCS.chat?.authHeaders()
            });
            VCS.state.chatAvatarHash = null;
            localStorage.removeItem('chat-avatar-hash');
            this.updateProfileUI();
            VCS.ui?.showToast?.(t('profilePhotoRemoved'), 'success');
        } catch (e) {
            console.error('[chat] avatar remove error:', e);
        }
    }

    // ── Private Methoden ─────────────────────────────────────

    _updateChatUnreadBadge() {
        const badge = $('#navChatBadge')[0];
        if (!badge) {
            return;
        }
        const total = Object.values(VCS.state.chatUnreadCounts).reduce((sum, n) => sum + n, 0);
        if (total > 0) {
            badge.textContent = total;
            badge.classList.remove('hidden');
        } else {
            badge.classList.add('hidden');
        }
    }
};
